import moment from 'moment';

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
/*  Latitude/longitude spherical geodesy formulae & scripts (c) Chris Veness 2002-2012            */
/*   - www.movable-type.co.uk/scripts/latlong.html                                                */
/*                                                                                                */
/*  Sample usage:                                                                                 */
/*    var p1 = new LatLon(51.5136, -0.0983);                                                      */
/*    var p2 = new LatLon(51.4778, -0.0015);                                                      */
/*    var dist = p1.distanceTo(p2);          // in km                                             */
/*    var brng = p1.bearingTo(p2);           // in degrees clockwise from north                   */
/*    ... etc                                                                                     */
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
/*  Note that minimal error checking is performed in this example code!                           */
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */


/**
 * @requires Geo
 */


/**
 * Creates a point on the earth's surface at the supplied latitude / longitude
 *
 * @constructor
 * @param {Number} lat: latitude in numeric degrees
 * @param {Number} lon: longitude in numeric degrees
 * @param {Number} [rad=6371]: radius of earth if different value is required from standard 6,371km
 */

export function LatLon(lat, lon, rad) {
    if (typeof (rad) === 'undefined') rad = 6371;  // earth's mean radius in km
    rad = 6371;
    // only accept numbers or valid numeric strings
    this._lat = typeof (lat) === 'number' ? lat : typeof (lat) === 'string' && lat.trim() !== '' ? +lat : NaN;
    this._lon = typeof (lon) === 'number' ? lon : typeof (lon) === 'string' && lon.trim() !== '' ? +lon : NaN;
    this._radius = typeof (rad) === 'number' ? rad : typeof (rad) === 'string' && rad.trim() !== '' ? +rad : NaN;
}


/**
 * Returns the distance from this point to the supplied point, in km
 * (using Haversine formula)
 *
 * from: Haversine formula - R. W. Sinnott, "Virtues of the Haversine",
 *       Sky and Telescope, vol 68, no 2, 1984
 *
 * @param   {LatLon} point: Latitude/longitude of destination point
 * @param   {Number} [precision=4]: no of significant digits to use for returned value
 * @returns {Number} Distance in km between this point and destination point
 */
LatLon.prototype.distanceTo = function (point, precision) {
    // default 4 sig figs reflects typical 0.3% accuracy of spherical model
    if (typeof precision === 'undefined') precision = 4;
    var R = this._radius;
    var lat1 = this._lat.toRad(), lon1 = this._lon.toRad();
    var lat2 = point._lat.toRad(), lon2 = point._lon.toRad();
    var dLat = lat2 - lat1;
    var dLon = lon2 - lon1;

    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c;
    return d;
    //return d.toPrecisionFixed(precision);
}


/**
 * Returns the (initial) bearing from this point to the supplied point, in degrees
 *   see http://williams.best.vwh.net/avform.htm#Crs
 *
 * @param   {LatLon} point: Latitude/longitude of destination point
 * @returns {Number} Initial bearing in degrees from North
 */
LatLon.prototype.bearingTo = function (point) {
    var lat1 = this._lat.toRad(), lat2 = point._lat.toRad();
    var dLon = (point._lon - this._lon).toRad();

    var y = Math.sin(dLon) * Math.cos(lat2);
    var x = Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    var brng = Math.atan2(y, x);

    return (brng.toDeg() + 360) % 360;
}


/**
 * Returns final bearing arriving at supplied destination point from this point; the final bearing
 * will differ from the initial bearing by varying degrees according to distance and latitude
 *
 * @param   {LatLon} point: Latitude/longitude of destination point
 * @returns {Number} Final bearing in degrees from North
 */
LatLon.prototype.finalBearingTo = function (point) {
    // get initial bearing from supplied point back to this point...
    var lat1 = point._lat.toRad(), lat2 = this._lat.toRad();
    var dLon = (this._lon - point._lon).toRad();

    var y = Math.sin(dLon) * Math.cos(lat2);
    var x = Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    var brng = Math.atan2(y, x);

    // ... & reverse it by adding 180°
    return (brng.toDeg() + 180) % 360;
}


/**
 * Returns the midpoint between this point and the supplied point.
 *   see http://mathforum.org/library/drmath/view/51822.html for derivation
 *
 * @param   {LatLon} point: Latitude/longitude of destination point
 * @returns {LatLon} Midpoint between this point and the supplied point
 */
LatLon.prototype.midpointTo = function (point) {
    const lat1 = this._lat.toRad(), lon1 = this._lon.toRad();
    const lat2 = point._lat.toRad();
    var dLon = (point._lon - this._lon).toRad();

    var Bx = Math.cos(lat2) * Math.cos(dLon);
    var By = Math.cos(lat2) * Math.sin(dLon);

    const lat3 = Math.atan2(Math.sin(lat1) + Math.sin(lat2),
        Math.sqrt((Math.cos(lat1) + Bx) * (Math.cos(lat1) + Bx) + By * By));
    var lon3 = lon1 + Math.atan2(By, Math.cos(lat1) + Bx);
    lon3 = (lon3 + 3 * Math.PI) % (2 * Math.PI) - Math.PI;  // normalise to -180..+180º

    return new LatLon(lat3.toDeg(), lon3.toDeg());
}


/**
 * Returns the destination point from this point having travelled the given distance (in km) on the
 * given initial bearing (bearing may vary before destination is reached)
 *
 *   see http://williams.best.vwh.net/avform.htm#LL
 *
 * @param   {Number} brng: Initial bearing in degrees
 * @param   {Number} dist: Distance in km
 * @returns {LatLon} Destination point
 */
LatLon.prototype.destinationPoint = function (brng, dist) {
    dist = typeof (dist) === 'number' ? dist : typeof (dist) === 'string' && dist.trim() !== '' ? +dist : NaN;
    dist = dist / this._radius;  // convert dist to angular distance in radians
    brng = brng.toRad();  //
    var lat1 = this._lat.toRad(), lon1 = this._lon.toRad();

    var lat2 = Math.asin(Math.sin(lat1) * Math.cos(dist) +
        Math.cos(lat1) * Math.sin(dist) * Math.cos(brng));
    var lon2 = lon1 + Math.atan2(Math.sin(brng) * Math.sin(dist) * Math.cos(lat1),
        Math.cos(dist) - Math.sin(lat1) * Math.sin(lat2));
    lon2 = (lon2 + 3 * Math.PI) % (2 * Math.PI) - Math.PI;  // normalise to -180..+180º

    return new LatLon(lat2.toDeg(), lon2.toDeg());
}


/**
 * Returns the point of intersection of two paths defined by point and bearing
 *
 *   see http://williams.best.vwh.net/avform.htm#Intersection
 *
 * @param   {LatLon} p1: First point
 * @param   {Number} brng1: Initial bearing from first point
 * @param   {LatLon} p2: Second point
 * @param   {Number} brng2: Initial bearing from second point
 * @returns {LatLon} Destination point (null if no unique intersection defined)
 */
LatLon.intersection = function (p1, brng1, p2, brng2) {
    brng1 = typeof brng1 === 'number' ? brng1 : typeof brng1 === 'string' && brng1.trim() !== '' ? +brng1 : NaN;
    brng2 = typeof brng2 === 'number' ? brng2 : typeof brng2 === 'string' && brng2.trim() !== '' ? +brng2 : NaN;
    var lat1 = p1._lat.toRad(), lon1 = p1._lon.toRad();
    var lat2 = p2._lat.toRad(), lon2 = p2._lon.toRad();
    var brng13 = brng1.toRad(), brng23 = brng2.toRad();
    var dLat = lat2 - lat1, dLon = lon2 - lon1;

    var dist12 = 2 * Math.asin(Math.sqrt(Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2)));
    if (dist12 === 0) return null;

    // initial/final bearings between points
    var brngA = Math.acos((Math.sin(lat2) - Math.sin(lat1) * Math.cos(dist12)) /
        (Math.sin(dist12) * Math.cos(lat1)));
    if (isNaN(brngA)) brngA = 0;  // protect against rounding
    var brngB = Math.acos((Math.sin(lat1) - Math.sin(lat2) * Math.cos(dist12)) /
        (Math.sin(dist12) * Math.cos(lat2)));

    if (Math.sin(lon2 - lon1) > 0) {
        var brng12 = brngA;
        var brng21 = 2 * Math.PI - brngB;
    } else {
        var brng12 = 2 * Math.PI - brngA;
        var brng21 = brngB;
    }

    var alpha1 = (brng13 - brng12 + Math.PI) % (2 * Math.PI) - Math.PI;  // angle 2-1-3
    var alpha2 = (brng21 - brng23 + Math.PI) % (2 * Math.PI) - Math.PI;  // angle 1-2-3

    if (Math.sin(alpha1) === 0 && Math.sin(alpha2) === 0) return null;  // infinite intersections
    if (Math.sin(alpha1) * Math.sin(alpha2) < 0) return null;       // ambiguous intersection

    //alpha1 = Math.abs(alpha1);
    //alpha2 = Math.abs(alpha2);
    // ... Ed Williams takes abs of alpha1/alpha2, but seems to break calculation?

    var alpha3 = Math.acos(-Math.cos(alpha1) * Math.cos(alpha2) +
        Math.sin(alpha1) * Math.sin(alpha2) * Math.cos(dist12));
    var dist13 = Math.atan2(Math.sin(dist12) * Math.sin(alpha1) * Math.sin(alpha2),
        Math.cos(alpha2) + Math.cos(alpha1) * Math.cos(alpha3))
    var lat3 = Math.asin(Math.sin(lat1) * Math.cos(dist13) +
        Math.cos(lat1) * Math.sin(dist13) * Math.cos(brng13));
    var dLon13 = Math.atan2(Math.sin(brng13) * Math.sin(dist13) * Math.cos(lat1),
        Math.cos(dist13) - Math.sin(lat1) * Math.sin(lat3));
    var lon3 = lon1 + dLon13;
    lon3 = (lon3 + 3 * Math.PI) % (2 * Math.PI) - Math.PI;  // normalise to -180..+180º

    return new LatLon(lat3.toDeg(), lon3.toDeg());
}


/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

/**
 * Returns the distance from this point to the supplied point, in km, travelling along a rhumb line
 *
 *   see http://williams.best.vwh.net/avform.htm#Rhumb
 *
 * @param   {LatLon} point: Latitude/longitude of destination point
 * @returns {Number} Distance in km between this point and destination point
 */
LatLon.prototype.rhumbDistanceTo = function (point) {
    var R = this._radius;
    var lat1 = this._lat.toRad(), lat2 = point._lat.toRad();
    var dLat = (point._lat - this._lat).toRad();
    var dLon = Math.abs(point._lon - this._lon).toRad();

    var dPhi = Math.log(Math.tan(lat2 / 2 + Math.PI / 4) / Math.tan(lat1 / 2 + Math.PI / 4));
    var q = (isFinite(dLat / dPhi)) ? dLat / dPhi : Math.cos(lat1);  // E-W line gives dPhi=0

    // if dLon over 180° take shorter rhumb across anti-meridian:
    if (Math.abs(dLon) > Math.PI) {
        dLon = dLon > 0 ? -(2 * Math.PI - dLon) : (2 * Math.PI + dLon);
    }

    var dist = Math.sqrt(dLat * dLat + q * q * dLon * dLon) * R;

    return dist.toPrecisionFixed(4);  // 4 sig figs reflects typical 0.3% accuracy of spherical model
}

/**
 * Returns the bearing from this point to the supplied point along a rhumb line, in degrees
 *
 * @param   {LatLon} point: Latitude/longitude of destination point
 * @returns {Number} Bearing in degrees from North
 */
LatLon.prototype.rhumbBearingTo = function (point) {
    var lat1 = this._lat.toRad(), lat2 = point._lat.toRad();
    var dLon = (point._lon - this._lon).toRad();

    var dPhi = Math.log(Math.tan(lat2 / 2 + Math.PI / 4) / Math.tan(lat1 / 2 + Math.PI / 4));
    if (Math.abs(dLon) > Math.PI) dLon = dLon > 0 ? -(2 * Math.PI - dLon) : (2 * Math.PI + dLon);
    var brng = Math.atan2(dLon, dPhi);

    return (brng.toDeg() + 360) % 360;
}

/**
 * Returns the destination point from this point having travelled the given distance (in km) on the
 * given bearing along a rhumb line
 *
 * @param   {Number} brng: Bearing in degrees from North
 * @param   {Number} dist: Distance in km
 * @returns {LatLon} Destination point
 */
LatLon.prototype.rhumbDestinationPoint = function (brng, dist) {
    var R = this._radius;
    var d = parseFloat(dist) / R;  // d = angular distance covered on earth’s surface
    var lat1 = this._lat.toRad(), lon1 = this._lon.toRad();
    brng = brng.toRad();

    var dLat = d * Math.cos(brng);
    // nasty kludge to overcome ill-conditioned results around parallels of latitude:
    if (Math.abs(dLat) < 1e-10) dLat = 0; // dLat < 1 mm

    var lat2 = lat1 + dLat;
    var dPhi = Math.log(Math.tan(lat2 / 2 + Math.PI / 4) / Math.tan(lat1 / 2 + Math.PI / 4));
    var q = (isFinite(dLat / dPhi)) ? dLat / dPhi : Math.cos(lat1);  // E-W line gives dPhi=0
    var dLon = d * Math.sin(brng) / q;

    // check for some daft bugger going past the pole, normalise latitude if so
    if (Math.abs(lat2) > Math.PI / 2) lat2 = lat2 > 0 ? Math.PI - lat2 : -Math.PI - lat2;

    var lon2 = (lon1 + dLon + 3 * Math.PI) % (2 * Math.PI) - Math.PI;

    return new LatLon(lat2.toDeg(), lon2.toDeg());
}

/**
 * Returns the loxodromic midpoint (along a rhumb line) between this point and the supplied point.
 *   see http://mathforum.org/kb/message.jspa?messageID=148837
 *
 * @param   {LatLon} point: Latitude/longitude of destination point
 * @returns {LatLon} Midpoint between this point and the supplied point
 */
LatLon.prototype.rhumbMidpointTo = function (point) {
    var lat1 = this._lat.toRad(), lon1 = this._lon.toRad();
    var lat2 = point._lat.toRad(), lon2 = point._lon.toRad();

    if (Math.abs(lon2 - lon1) > Math.PI) lon1 += 2 * Math.PI; // crossing anti-meridian

    var lat3 = (lat1 + lat2) / 2;
    var f1 = Math.tan(Math.PI / 4 + lat1 / 2);
    var f2 = Math.tan(Math.PI / 4 + lat2 / 2);
    var f3 = Math.tan(Math.PI / 4 + lat3 / 2);
    var lon3 = ((lon2 - lon1) * Math.log(f3) + lon1 * Math.log(f2) - lon2 * Math.log(f1)) / Math.log(f2 / f1);

    if (!isFinite(lon3)) lon3 = (lon1 + lon2) / 2; // parallel of latitude

    lon3 = (lon3 + 3 * Math.PI) % (2 * Math.PI) - Math.PI;  // normalise to -180..+180º

    return new LatLon(lat3.toDeg(), lon3.toDeg());
}


/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */


/**
 * Returns the latitude of this point; signed numeric degrees if no format, otherwise format & dp
 * as per Geo.toLat()
 *
 * @param   {String} [format]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to display
 * @returns {Number|String} Numeric degrees if no format specified, otherwise deg/min/sec
 */
LatLon.prototype.lat = function (format, dp) {
    if (typeof format === 'undefined') return this._lat;

    return Geo.toLat(this._lat, format, dp);
}

/**
 * Returns the longitude of this point; signed numeric degrees if no format, otherwise format & dp
 * as per Geo.toLon()
 *
 * @param   {String} [format]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to display
 * @returns {Number|String} Numeric degrees if no format specified, otherwise deg/min/sec
 */
LatLon.prototype.lon = function (format, dp) {
    if (typeof format === 'undefined') return this._lon;

    return Geo.toLon(this._lon, format, dp);
}

/**
 * Returns a string representation of this point; format and dp as per lat()/lon()
 *
 * @param   {String} [format]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to display
 * @returns {String} Comma-separated latitude/longitude
 */
LatLon.prototype.toString = function (format, dp) {
    if (typeof format === 'undefined') format = 'dms';

    return Geo.toLat(this._lat, format, dp) + ', ' + Geo.toLon(this._lon, format, dp);
}

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

// ---- extend Number object with methods for converting degrees/radians

/** Converts numeric degrees to radians */
if (typeof Number.prototype.toRad === 'undefined') {
    Number.prototype.toRad = function () {
        return this * Math.PI / 180;
    }
}

/** Converts radians to numeric (signed) degrees */
if (typeof Number.prototype.toDeg === 'undefined') {
    Number.prototype.toDeg = function () {
        return this * 180 / Math.PI;
    }
}

/**
 * Formats the significant digits of a number, using only fixed-point notation (no exponential)
 *
 * @param   {Number} precision: Number of significant digits to appear in the returned string
 * @returns {String} A string representation of number which contains precision significant digits
 */
if (typeof Number.prototype.toPrecisionFixed === 'undefined') {
    Number.prototype.toPrecisionFixed = function (precision) {

        // use standard toPrecision method
        var n = this.toPrecision(precision);

        // ... but replace +ve exponential format with trailing zeros
        n = n.replace(/(.+)e\+(.+)/, function (n, sig, exp) {
            sig = sig.replace(/\./, '');       // remove decimal from significand
            var l = sig.length - 1;
            while (exp-- > l) sig = sig + '0'; // append zeros from exponent
            return sig;
        });

        // ... and replace -ve exponential format with leading zeros
        n = n.replace(/(.+)e-(.+)/, function (n, sig, exp) {
            sig = sig.replace(/\./, '');       // remove decimal from significand
            while (exp-- > 1) sig = '0' + sig; // prepend zeros from exponent
            return '0.' + sig;
        });

        return n;
    }
}

/** Trims whitespace from string (q.v. blog.stevenlevithan.com/archives/faster-trim-javascript) */
if (typeof String.prototype.trim === 'undefined') {
    String.prototype.trim = function () {
        return String(this).replace(/^\s\s*/, '').replace(/\s\s*$/, '');
    }
}


/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
if (!window.console) window.console = { log: function () { } };
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
/*  Geodesy representation conversion functions (c) Chris Veness 2002-2012                        */
/*   - www.movable-type.co.uk/scripts/latlong.html                                                */
/*                                                                                                */
/*  Sample usage:                                                                                 */
/*    var lat = Geo.parseDMS('51° 28′ 40.12″ N');                                                 */
/*    var lon = Geo.parseDMS('000° 00′ 05.31″ W');                                                */
/*    var p1 = new LatLon(lat, lon);                                                              */
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */


var Geo = {};  // Geo namespace, representing static class


/**
 * Parses string representing degrees/minutes/seconds into numeric degrees
 *
 * This is very flexible on formats, allowing signed decimal degrees, or deg-min-sec optionally
 * suffixed by compass direction (NSEW). A variety of separators are accepted (eg 3º 37' 09"W)
 * or fixed-width format without separators (eg 0033709W). Seconds and minutes may be omitted.
 * (Note minimal validation is done).
 *
 * @param   {String|Number} dmsStr: Degrees or deg/min/sec in variety of formats
 * @returns {Number} Degrees as decimal number
 * @throws  {TypeError} dmsStr is an object, perhaps DOM object without .value?
 */
Geo.parseDMS = function (dmsStr) {
    var deg;
    if (typeof deg === 'object') throw new TypeError('Geo.parseDMS - dmsStr is [DOM?] object');

    // check for signed decimal degrees without NSEW, if so return it directly
    if (typeof dmsStr === 'number' && isFinite(dmsStr)) return Number(dmsStr);

    // strip off any sign or compass dir'n & split out separate d/m/s
    var dms = String(dmsStr).trim().replace(/^-/, '').replace(/[NSEW]$/i, '').split(/[^0-9.,]+/);
    if (dms[dms.length - 1] === '') dms.splice(dms.length - 1);  // from trailing symbol

    if (dms === '') return NaN;

    // and convert to decimal degrees...
    switch (dms.length) {
        case 3:  // interpret 3-part result as d/m/s
            deg = dms[0] / 1 + dms[1] / 60 + dms[2] / 3600;
            break;
        case 2:  // interpret 2-part result as d/m
            deg = dms[0] / 1 + dms[1] / 60;
            break;
        case 1:  // just d (possibly decimal) or non-separated dddmmss
            deg = dms[0];
            // check for fixed-width unseparated format eg 0033709W
            //if (/[NS]/i.test(dmsStr)) deg = '0' + deg;  // - normalise N/S to 3-digit degrees
            //if (/[0-9]{7}/.test(deg)) deg = deg.slice(0,3)/1 + deg.slice(3,5)/60 + deg.slice(5)/3600;
            break;
        default:
            return NaN;
    }
    if (/^-|[WS]$/i.test(dmsStr.trim())) deg = -deg; // take '-', west and south as -ve
    return Number(deg);
}


/**
 * Checks if string representing degrees/minutes/seconds can be converted
 *
 * This is very flexible on formats, allowing signed decimal degrees, or deg-min-sec optionally
 * suffixed by compass direction (NSEW). A variety of separators are accepted (eg 3º 37' 09"W)
 * or fixed-width format without separators (eg 0033709W). Seconds and minutes may be omitted.
 * (Note minimal validation is done).
 *
 * @param   {String|Number} dmsStr: Degrees or deg/min/sec in variety of formats
 * @returns {Number} Degrees as decimal number
 * @throws  {TypeError} dmsStr is an object, perhaps DOM object without .value?
 */
Geo.checkDMS = function (dmsStr) {
    var deg;
    if (typeof deg === 'object') throw new TypeError('Geo.parseDMS - dmsStr is [DOM?] object');

    // check for signed decimal degrees without NSEW, if so return it directly
    if (typeof dmsStr === 'number' && isFinite(dmsStr)) return Number(dmsStr);

    // strip off any sign or compass dir'n & split out separate d/m/s
    var dms = String(dmsStr).trim().replace(/^-/, '').replace(/[NSEW]$/i, '').split(/[^0-9.,]+/);
    if (dms[dms.length - 1] === '') dms.splice(dms.length - 1);  // from trailing symbol

    if (dms === '') return 'False';

    // and convert to decimal degrees...
    switch (dms.length) {
        case 3:  // interpret 3-part result as d/m/s
            deg = dms[0] / 1 + dms[1] / 60 + dms[2] / 3600;
            break;
        case 2:  // interpret 2-part result as d/m
            deg = dms[0] / 1 + dms[1] / 60;
            break;
        case 1:  // just d (possibly decimal) or non-separated dddmmss
            deg = dms[0];
            // check for fixed-width unseparated format eg 0033709W
            //if (/[NS]/i.test(dmsStr)) deg = '0' + deg;  // - normalise N/S to 3-digit degrees
            //if (/[0-9]{7}/.test(deg)) deg = deg.slice(0,3)/1 + deg.slice(3,5)/60 + deg.slice(5)/3600;
            break;
        default:
            return 'False';
    }
    if (/^-|[WS]$/i.test(dmsStr.trim())) deg = -deg; // take '-', west and south as -ve
    return 'True';
}


/**
 * Convert decimal degrees to deg/min/sec format
 *  - degree, prime, double-prime symbols are added, but sign is discarded, though no compass
 *    direction is added
 *
 * @private
 * @param   {Number} deg: Degrees
 * @param   {String} [format=dms]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to use - default 0 for dms, 2 for dm, 4 for d
 * @returns {String} deg formatted as deg/min/secs according to specified format
 * @throws  {TypeError} deg is an object, perhaps DOM object without .value?
 */
Geo.toDMS = function (deg, format, dp) {
    if (typeof deg === 'object') throw new TypeError('Geo.toDMS - deg is [DOM?] object');
    if (isNaN(deg)) return null;  // give up here if we can't make a number from deg
    format = format.toLowerCase();

    // default values
    if (typeof format === 'undefined') format = 'dms';
    if (typeof dp === 'undefined') {
        switch (format) {
            case 'd':
            case 'dd':
                dp = 4; break;
            case 'ddm': dp = 2; break;
            case 'dms': dp = 0; break;
            default: format = 'dms'; dp = 0;  // be forgiving on invalid format
        }
    }

    deg = Math.abs(deg);  // (unsigned result ready for appending compass dir'n)

    var dms = '';
    var s = '';
    var sec = '';
    var symbol = '';
    switch (format) {
        case 'd':
        case 'dd':
            d = deg.toFixed(dp);     // round degrees
            if (d < 100) d = '0' + d;  // pad with leading zeros
            if (d < 10) d = '0' + d;
            dms = d + '\u00B0';      // add º symbol
            break;
        case 'ddm':
            var degconvert = deg;
            if (deg < 0) {
                degconvert = deg / -1;
            }
            var d = parseInt(Math.floor(degconvert)); //Get just the int to get the degrees.  Round down.
            var m = ((degconvert - parseInt(Math.floor(degconvert))) * 60).toFixed(3); //Get anything after the decimal place, multiply it by 60 and get three decimal places
            if (m >= 60) // If the minutes are more than or equal to 60, make them zero and add one to the degrees
            {
                m = 0;
                d = d + 1;
            }
            var vDegree = d;
            var vMinute = m;
            if (m === 0) //Ensure we've always got three decimal places in the minutes
            {
                vMinute = "0.000"
            }
            dms = vDegree + "°" + vMinute + "'";
            break;
        case 'dms':
            sec = (deg * 3600).toFixed(dp);  // convert degrees to seconds & round
            d = Math.floor(sec / 3600);    // get component deg/min/sec
            m = Math.floor(sec / 60) % 60;
            s = (sec % 60).toFixed(dp);    // pad with trailing zeros
            if (d < 100) d = '0' + d;            // pad with leading zeros
            if (d < 10) d = '0' + d;
            if (m < 10) m = '0' + m;
            if (s < 10) s = '0' + s;
            dms = d + '\u00B0' + m + '\u2032' + s + '\u2033';  // add º, ', " symbols
            break;
        case 'delimited':
            sec = (deg * 3600).toFixed(dp);  // convert degrees to seconds & round
            d = Math.floor(sec / 3600);    // get component deg/min/sec
            m = Math.floor(sec / 60) % 60;
            s = (sec % 60).toFixed(dp);    // pad with trailing zeros
            if (d < 100) d = '0' + d;            // pad with leading zeros
            if (d < 10) d = '0' + d;
            if (m < 10) m = '0' + m;
            if (s < 10) s = '0' + s;
            dms = d + '|' + m + '|' + s;  // delimited with '|'
            break;
    }

    return dms;
}

/**
 * Convert decimal degrees to deg/min/sec format
 *  - degree, prime, double-prime symbols are added, but sign is discarded, compass direction is added
 *
 * @private
 * @param   {Number} deg: Degrees
 * @param   {String} [format=dms]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to use - default 0 for dms, 2 for dm, 4 for d
 * @returns {String} deg formatted as deg/min/secs according to specified format
 * @throws  {TypeError} deg is an object, perhaps DOM object without .value?
 */
Geo.toDMSDir = function (deg, format, latlng, dp) {
    if (typeof deg === 'object') throw new TypeError('Geo.toDMS - deg is [DOM?] object');
    if (isNaN(deg)) return null;  // give up here if we can't make a number from deg
    format = format.toLowerCase();

    // default values
    if (typeof format === 'undefined') format = 'dms';
    if (typeof dp === 'undefined') {
        switch (format) {
            case 'd':
            case 'dd':
                dp = 4; break;
            case 'ddm': dp = 2; break;
            case 'dms': dp = 0; break;
            default: format = 'dms'; dp = 0;  // be forgiving on invalid format
        }
    }

    var original = deg;
    deg = Math.abs(deg);  // (unsigned result ready for appending compass dir'n)

    var dms = '';
    var s = '';
    var sec = '';
    var symbol = '';
    switch (format) {
        case 'd':
            dms = original.toFixed(dp);
            break;
        case 'dd':
            d = deg.toFixed(dp);     // round degrees
            if (d < 100) d = '0' + d;  // pad with leading zeros
            if (d < 10) d = '0' + d;
            symbol = latlng === "lat" ? (original < 0 ? "S " : "N ") : (original < 0 ? "W " : "E ");
            dms = symbol + d + '\u00B0';      // add º symbol
            break;
        case 'ddm':
            var degconvert = deg;
            if (deg < 0) {
                degconvert = deg / -1;
            }
            var d = parseInt(Math.floor(degconvert)); //Get just the int to get the degrees.  Round down.
            var m = ((degconvert - parseInt(Math.floor(degconvert))) * 60).toFixed(3); //Get anything after the decimal place, multiply it by 60 and get three decimal places
            if (m >= 60) // If the minutes are more than or equal to 60, make them zero and add one to the degrees
            {
                m = 0;
                d = d + 1;
            }
            var vDegree = d;
            var vMinute = m;
            if (m === 0) //Ensure we've always got three decimal places in the minutes
            {
                vMinute = "0.000"
            }
            symbol = latlng === "lat" ? (original < 0 ? "S " : "N ") : (original < 0 ? "W " : "E ");
            dms = symbol + vDegree + "°" + vMinute + "'";
            break;
        case 'dms':
            sec = (deg * 3600).toFixed(dp);  // convert degrees to seconds & round
            d = Math.floor(sec / 3600);    // get component deg/min/sec
            m = Math.floor(sec / 60) % 60;
            s = (sec % 60).toFixed(dp);    // pad with trailing zeros
            if (d < 100) d = '0' + d;            // pad with leading zeros
            if (d < 10) d = '0' + d;
            if (m < 10) m = '0' + m;
            if (s < 10) s = '0' + s;
            symbol = latlng === "lat" ? (original < 0 ? "S " : "N ") : (original < 0 ? "W " : "E ");
            dms = symbol + d + '\u00B0' + m + '\u2032' + s + '\u2033';  // add º, ', " symbols
            break;
        case 'delimited':
            sec = (deg * 3600).toFixed(dp);  // convert degrees to seconds & round
            d = Math.floor(sec / 3600);    // get component deg/min/sec
            m = Math.floor(sec / 60) % 60;
            s = (sec % 60).toFixed(dp);    // pad with trailing zeros
            if (d < 100) d = '0' + d;            // pad with leading zeros
            if (d < 10) d = '0' + d;
            if (m < 10) m = '0' + m;
            if (s < 10) s = '0' + s;
            dms = d + '|' + m + '|' + s;  // delimited with '|'
            break;
    }

    return dms;
}




/**
 * Convert numeric degrees to deg/min/sec latitude (suffixed with N/S)
 *
 * @param   {Number} deg: Degrees
 * @param   {String} [format=dms]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to use - default 0 for dms, 2 for dm, 4 for d
 * @returns {String} Deg/min/seconds
 */
Geo.toLat = function (deg, format, dp) {
    var lat = Geo.toDMS(deg, format, dp);
    format = format.toLowerCase();
    switch (format) {
        case 'dms':
            return lat === null ? '–' : lat.slice(1) + (deg < 0 ? 'S' : 'N');  // knock off initial '0' for lat!
        case 'dd':
            return deg;
        default:
            return lat === null ? '–' : lat + (deg < 0 ? 'S' : 'N');  // knock off initial '0' for lat!
    }
}


/**
 * Convert numeric degrees to deg/min/sec longitude (suffixed with E/W)
 *
 * @param   {Number} deg: Degrees
 * @param   {String} [format=dms]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to use - default 0 for dms, 2 for dm, 4 for d
 * @returns {String} Deg/min/seconds
 */
Geo.toLon = function (deg, format, dp) {
    format = format.toLowerCase();
    var lon = Geo.toDMS(deg, format, dp);
    switch (format) {
        case 'dd':
            return deg;
        default:
            return lon === null ? '–' : lon + (deg < 0 ? 'W' : 'E');
    }

}


/**
 * Convert numeric degrees to deg/min/sec as a bearing (0º..360º)
 *
 * @param   {Number} deg: Degrees
 * @param   {String} [format=dms]: Return value as 'd', 'dm', 'dms'
 * @param   {Number} [dp=0|2|4]: No of decimal places to use - default 0 for dms, 2 for dm, 4 for d
 * @returns {String} Deg/min/seconds
 */
Geo.toBrng = function (deg, format, dp) {
    deg = (Number(deg) + 360) % 360;  // normalise -ve values to 180º..360º
    var brng = Geo.toDMS(deg, format, dp);
    return brng === null ? '–' : brng.replace('360', '0');  // just in case rounding took us up to 360º!
}


/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
if (!window.console) window.console = { log: function () { } };


function dmsString(dmsString) {

    dmsString = dmsString.trim();

    // Inspired by https://gist.github.com/JeffJacobson/2955437
    // See https://regex101.com/r/kS2zR1/3
    var dmsRe = /([NSEW])?(-)?(\d+(?:\.\d+)?)[°º:d\s]?\s?(?:(\d+(?:\.\d+)?)['’‘′:]\s?(?:(\d{1,2}(?:\.\d+)?)(?:"|″|’’|'')?)?)?\s?([NSEW])?/i;

    var result = {};

    var m1, m2, decDeg1, decDeg2, dmsString2;

    m1 = dmsString.match(dmsRe);

    if (!m1) throw 'Could not parse string';

    // If dmsString starts with a hemisphere letter, then the regex can also capture the
    // hemisphere letter for the second coordinate pair if also in the string
    if (m1[1]) {
        m1[6] = undefined;
        dmsString2 = dmsString.substr(m1[0].length - 1).trim();
    } else {
        dmsString2 = dmsString.substr(m1[0].length).trim();
    }

    decDeg1 = decDegFromMatch(m1);

    m2 = dmsString2.match(dmsRe);

    decDeg2 = m2 ? decDegFromMatch(m2) : {};

    if (typeof decDeg1.latLon === 'undefined') {
        if (!isNaN(decDeg1.decDeg) && isNaN(decDeg2.decDeg)) {
            // If we only have one coordinate but we have no hemisphere value,
            // just return the decDeg number
            return decDeg1.decDeg;
        } else if (!isNaN(decDeg1.decDeg) && !isNaN(decDeg2.decDeg)) {
            // If no hemisphere letter but we have two coordinates,
            // infer that the first is lat, the second lon
            decDeg1.latLon = 'lat';
            decDeg2.latLon = 'lon';
        } else {
            throw 'Could not parse string';
        }
    }

    // If we parsed the first coordinate as lat or lon, then assume the second is the other
    if (typeof decDeg2.latLon === 'undefined') {
        decDeg2.latLon = decDeg1.latLon === 'lat' ? 'lon' : 'lat';
    }

    result[decDeg1.latLon] = decDeg1.decDeg;
    result[decDeg2.latLon] = decDeg2.decDeg;

    return result;

}

function decDegFromMatch(m) {
    var signIndex = {
        "-": -1,
        "N": 1,
        "S": -1,
        "E": 1,
        "W": -1
    };

    var latLonIndex = {
        "N": "lat",
        "S": "lat",
        "E": "lon",
        "W": "lon"
    };
    var degrees, minutes, seconds, sign, latLon;

    sign = signIndex[m[2]] || signIndex[m[1]] || signIndex[m[6]] || 1;
    degrees = Number(m[3]);
    minutes = m[4] ? Number(m[4]) : 0;
    seconds = m[5] ? Number(m[5]) : 0;
    latLon = latLonIndex[m[1]] || latLonIndex[m[6]];

    if (!inRange(degrees, 0, 180)) throw 'Degrees out of range';
    if (!inRange(minutes, 0, 60)) throw 'Minutes out of range';
    if (!inRange(seconds, 0, 60)) throw 'Seconds out of range';

    return {
        decDeg: sign * (degrees + minutes / 60 + seconds / 3600),
        latLon: latLon
    };
}

function inRange(value, a, b) {
    return value >= a && value <= b;
}

function SplitTime(numberOfHours) {
    var days = Math.floor(numberOfHours / 24);
    var remainder = numberOfHours % 24;
    var hours = Math.floor(remainder);
    var minutes = Math.floor(60 * (remainder - hours));
    return ({ "days": days, "hours": hours, "minutes": minutes })
}

function getRouteTemplateScaling(feature_collection, newEtd, newEta, originalEtd, originalEta, waitOptions, speedOptions) {
    var wait_hours = 0;
    var distance_km = 0;
    for (let i = 0; i < feature_collection.features.length - 1; i++) {
        var thisItem = feature_collection.features[i];
        var nextItem = feature_collection.features[i + 1];
        wait_hours += thisItem.properties.wait_time_hours;
        var p1 = new LatLon(thisItem.geometry.coordinates[1], thisItem.geometry.coordinates[0]);
        var p2 = new LatLon(nextItem.geometry.coordinates[1], nextItem.geometry.coordinates[0]);
        distance_km += parseFloat(p1.distanceTo(p2));

    }

    //Get the original time taken for the route
    var originalStart = moment(originalEtd);
    var originalEnd = moment(originalEta);
    var originalDuration = moment.duration(originalEnd.diff(originalStart));

    //Get the new time taken for the route
    var newStart = moment(newEtd);
    var newEnd = moment(newEta);
    var newDuration = moment.duration(newEnd.diff(newStart));

    switch (waitOptions) {
        case "keep":
            if (wait_hours > 0 && newDuration.asHours() <= wait_hours) {
                return {
                    'template': null,
                    'distance_hours': null,
                    'error': 'You have allowed ' + (Math.round(newDuration.asHours() * 100) / 100) + ' hour' + (newDuration.asHours() == 1 ? '' : 's') +
                        ' for the route, but want to preserve ' + (Math.round(wait_hours * 100) / 100) + ' hour' + (wait_hours == 1 ? '' : 's') + ' total waiting time.  Route cannot be scaled.',
                    'etd': null,
                    'eta': null
                }
            }

            switch (speedOptions) {
                case "scale":
                    //Keeping wait times.  Scaling speed.
                    var wait_seconds = wait_hours * 60 * 60;
                    var original_seconds = originalDuration.asSeconds() - wait_seconds;
                    var new_seconds = newDuration.asSeconds() - wait_seconds;
                    var scale = original_seconds / new_seconds;
                    var original_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    for (let i = 0; i < feature_collection.features.length -1; i++) {
                        feature_collection.features[i].properties.speed.Knots = feature_collection.features[i].properties.speed.Knots * scale;
                        feature_collection.features[i].properties.speed.Kph = feature_collection.features[i].properties.speed.Kph * scale;
                        feature_collection.features[i].properties.speed.Mph = feature_collection.features[i].properties.speed.Mph * scale;
                        feature_collection.features[i].properties.speed.Ms = feature_collection.features[i].properties.speed.Ms * scale;
                    }
                    var new_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    return {
                        'template': JSON.stringify(feature_collection),
                        'distance_hours': new_distance_hours,
                        'error': '',
                        'etd': newStart.toDate(),
                        'eta': CalculateETA(feature_collection, newStart.toDate())
                    }
                    break;
                case "equalise":
                    //Keeping wait times.  Equalising speed.
                    var wait_seconds = wait_hours * 60 * 60;
                    var original_seconds = originalDuration.asSeconds() - wait_seconds;
                    var new_seconds = newDuration.asSeconds() - wait_seconds;
                    var kmm = distance_km / (new_seconds / 60);
                    var knots = kmm * 32.3974;
                    var kph = knots * 1.852;
                    var mph = knots * 1.15078;
                    var ms = knots * 0.514444;
                    var original_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    for (let i = 0; i < feature_collection.features.length; i++) {
                        feature_collection.features[i].properties.speed.Knots = knots;
                        feature_collection.features[i].properties.speed.Kph = kph;
                        feature_collection.features[i].properties.speed.Mph = mph;
                        feature_collection.features[i].properties.speed.Ms = ms;
                    }
                    var new_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    return {
                        'template': JSON.stringify(feature_collection),
                        'distance_hours': new_distance_hours,
                        'error': '',
                        'etd': newStart.toDate(),
                        'eta': CalculateETA(feature_collection, newStart.toDate())
                    }
                    break;
            }
            break;
        case "clear":
            switch (speedOptions) {
                case "scale":
                    //Clearing wait times.  Scaling speed.
                    var wait_seconds = wait_hours * 60 * 60;
                    var original_seconds = originalDuration.asSeconds() - wait_seconds;
                    var new_seconds = newDuration.asSeconds();
                    var scale = original_seconds / new_seconds;
                    var original_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    for (let i = 0; i < feature_collection.features.length - 1; i++) {
                        feature_collection.features[i].properties.wait_time_hours = 0;
                        feature_collection.features[i].properties.speed.Knots = feature_collection.features[i].properties.speed.Knots * scale;
                        feature_collection.features[i].properties.speed.Kph = feature_collection.features[i].properties.speed.Kph * scale;
                        feature_collection.features[i].properties.speed.Mph = feature_collection.features[i].properties.speed.Mph * scale;
                        feature_collection.features[i].properties.speed.Ms = feature_collection.features[i].properties.speed.Ms * scale;
                    }
                    feature_collection.features[feature_collection.features.length - 1].properties.wait_time_hours = 0;
                    var new_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    return {
                        'template': JSON.stringify(feature_collection),
                        'distance_hours': new_distance_hours,
                        'error': '',
                        'etd': newStart.toDate(),
                        'eta': CalculateETA(feature_collection, newStart.toDate())
                    }
                    break;
                case "equalise":
                    //Clearing wait times.  Equalising speed.
                    var wait_seconds = wait_hours * 60 * 60;
                    var original_seconds = originalDuration.asSeconds() - wait_seconds;
                    var new_seconds = newDuration.asSeconds() - wait_seconds;
                    var kmm = distance_km / ((new_seconds / 60) + (wait_hours * 60));
                    var knots = kmm * 32.3974;
                    var kph = knots * 1.852;
                    var mph = knots * 1.15078;
                    var ms = knots * 0.514444;
                    var original_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    for (let i = 0; i < feature_collection.features.length; i++) {
                        feature_collection.features[i].properties.wait_time_hours = 0;
                        feature_collection.features[i].properties.speed.Knots = knots;
                        feature_collection.features[i].properties.speed.Kph = kph;
                        feature_collection.features[i].properties.speed.Mph = mph;
                        feature_collection.features[i].properties.speed.Ms = ms;
                    }
                    feature_collection.features[feature_collection.features.length - 1].properties.wait_time_hours = 0;
                    var new_distance_hours = CalculateRouteDistanceHours(feature_collection);
                    return {
                        'template': JSON.stringify(feature_collection),
                        'distance_hours': new_distance_hours,
                        'error': '',
                        'etd': newStart.toDate(),
                        'eta': CalculateETA(feature_collection, newStart.toDate())
                    }
                    break;
            }
            break;
    }
    return null;
}

function CalculateRouteDistanceHours(feature_collection) {
    var distance = 0;
    var hours = 0;
    for (var i = 0; i < feature_collection.features.length - 1; i++) {
        var from = feature_collection.features[i];
        var to = feature_collection.features[i + 1];
        var p1 = new LatLon(from.geometry.coordinates[1], from.geometry.coordinates[0]);
        var p2 = new LatLon(to.geometry.coordinates[1], to.geometry.coordinates[0]);
        var dist = parseFloat(p1.distanceTo(p2)) * 0.539956803; //(In NM)
        distance = distance + dist;
        hours = hours + (dist / from.properties.speed.Knots) + parseInt(from.properties.wait_time_hours);
    }
    return ({ "distance": distance, "hours": hours, "knots": distance / hours });
}

function CalculateETA(feature_collection, etd) {
    var DistanceHours = CalculateRouteDistanceHours(feature_collection);
    var newDate = new Date();
    return newDate.setTime(etd.getTime() + (DistanceHours.hours * 60 * 60 * 1000));
}

function CalculateETD(feature_collection, eta) {
    var DistanceHours = CalculateRouteDistanceHours(feature_collection);
    var newDate = new Date();
    return newDate.setTime(eta.getTime() - (DistanceHours.hours * 60 * 60 * 1000));
}